import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { parseAmount } from '../../util';
import SummaryCard from '../../components/shared/Charts/SummaryCard/SummaryCard';
import XIconWrapper from '../page-dashboard-V2/XIconWrapper/XIconWrapper';
import TemplateItem, { TemplateOptionType } from '../page-dashboard-V2/TemplateItem/TemplateItem';
import { OverviewItemType } from '../page-dashboard-V2/Tabs/Overview/OverviewTab';
import { UNIT } from '../../ts-components/page-dashboard-V2/helper';
import styles from './app-project-purchase-list.module.scss';
import { ServiceUser } from '../../services/service-user';
import appState from '../../state/AppStateContainer';
import { autosave } from '../../util';
import { useTranslation } from 'react-i18next';

interface SummationCardsProps {
  areas: any[];
}

type SummationCardType = {
  id: string;
  name: string;
  itemType: OverviewItemType;
  component: React.ReactNode;
} | null;

const SummationCards: React.FC<SummationCardsProps> = ({ areas }) => {
  const [t] = useTranslation('projects');

  const getContractSumFromRow = (rowData: any) => {
    const contractsSum = rowData.contracts?.filter((contract: any) => !!contract?.signedAt)?.reduce((sum: number, c: any) => sum + parseAmount(c.amount ?? ''), 0) ?? 0;
    const manualContractsSum = rowData.manualContract?.reduce((sum: number, c: any) => sum + parseAmount(c.amount ?? ''), 0) ?? 0;
    const sum = contractsSum + manualContractsSum;
    return sum;
  };

  const getProfitDataFromRow = (area: any) => {
    const cost = getContractSumFromRow(area);
    const additionsFactor = (100 + parseAmount(area.estimatedAdditionsPercent ?? "0")) / 100;
    const budget = parseAmount(area.budget ?? '') / additionsFactor;
    if (budget > 0 && cost > 0) {
      return [budget - cost, budget];
    }
  }

  const calculateTotalContractSum = (areas: any[]) => {
    return areas.reduce((total, area) => {
      const contractSum = getContractSumFromRow(area);
      return total + contractSum;
    }, 0);
  };

  const calculateTotalFinalCost = (areas: any[]) => {
    return areas.reduce((total, area) => {
      const finalCost = parseAmount(area.finalCost || '');
      return total + (isNaN(finalCost) ? 0 : finalCost);
    }, 0);
  };

  const calculateTotalBudget = (areas: any[]) => {
    return areas.reduce((total, area) => {
      const budget = parseAmount(area.budget || '');
      return total + (isNaN(budget) ? 0 : budget);
    }, 0);
  };

  const calculateTotalPurchaseBudget = (areas: any[]) => {
    return areas.reduce((total, area) => {
      const estimatedAdditionsPercent = parseAmount(area.estimatedAdditionsPercent ?? 0);
      const purchaseGoalPercent = parseAmount(area.purchaseGoalPercent ?? 0);
      const additionsFactor = (100 + estimatedAdditionsPercent) / 100;
      const goalFactor = (100 - purchaseGoalPercent) / 100;
      const budget = parseAmount(area.budget ?? 0);
      const purchaseBudget = budget / additionsFactor * goalFactor;
      return total + (isNaN(purchaseBudget) ? 0 : purchaseBudget);
    }, 0);
  };

  const calculateTotalPurchasingProfit = (areas: any[]) => {
    return areas.reduce((total, area) => {
      const data = getProfitDataFromRow(area);

      if (data) {
        const [profit] = data;
        return total + (isNaN(profit) ? 0 : profit);
      }

      return total;
    }, 0);
  };

  const calculateTotalPurchaseProfitPercentage = (areas: any[]) => {
    const areasWithBudgetAndContractSum = areas.filter(area => area?.budget && getContractSumFromRow(area) > 0);
    const totalPurchaseProfit = calculateTotalPurchasingProfit(areasWithBudgetAndContractSum);
    const totalPurchaseBudget = calculateTotalPurchaseBudget(areasWithBudgetAndContractSum);

    return Math.round(totalPurchaseProfit / totalPurchaseBudget * 100);
  };

  const calculateTotalETA = (areas: any[]) => {
    return areas.reduce((total, area) => {
      const contractSum = getContractSumFromRow(area);
      let difference = 0;
      if (contractSum && area?.finalCost) {
        difference = parseAmount(area.finalCost) - contractSum;
      }
      return total + (isNaN(difference) ? 0 : difference);
    }, 0);
  };

  const totalContractSum = useMemo(() => calculateTotalContractSum(areas), [areas]);
  const totalFinalCost = useMemo(() => calculateTotalFinalCost(areas), [areas]);
  const totalPurchasingProfit = useMemo(() => calculateTotalPurchasingProfit(areas), [areas]);
  const totalBudget = useMemo(() => calculateTotalBudget(areas), [areas]);
  const purchaseBudget = useMemo(() => calculateTotalPurchaseBudget(areas), [areas]);
  const totalETA = useMemo(() => calculateTotalETA(areas), [areas]);
  const purchasingProfitPercentage = useMemo(() => calculateTotalPurchaseProfitPercentage(areas), [areas]);

  const cardOptions: TemplateOptionType[] = useMemo(() => [
    {
      id: 'contractSum',
      name: t('summationCards.cards.contractSum.name', 'Total Kontraktssumma'),
      itemType: OverviewItemType.SMALL,
      component: (
        <SummaryCard
          title={t('summationCards.cards.contractSum.title', 'Total Kontraktssumma')}
          value={totalContractSum}
          unit={UNIT.AMOUNT}
          infoText={t('summationCards.cards.contractSum.infoText', 'Total kontraktssumma för alla signerade avtal')}
          size="small"
        />
      )
    },
    {
      id: 'finalCost',
      name: t('summationCards.cards.finalCost.name', 'Total Slutkostnad'),
      itemType: OverviewItemType.SMALL,
      component: (
        <SummaryCard
          title={t('summationCards.cards.finalCost.title', 'Total Slutkostnad')}
          value={totalFinalCost}
          unit={UNIT.AMOUNT}
          infoText={t('summationCards.cards.finalCost.infoText', 'Total slutkostnad för alla inköpskategorier')}
          size="small"
        />
      )
    },
    {
      id: 'purchasingBudget',
      name: t('summationCards.cards.purchasingBudget.name', 'Total inköpsbudget'),
      itemType: OverviewItemType.SMALL,
      component: (
        <SummaryCard
          title={t('summationCards.cards.purchasingBudget.title', 'Total inköpsbudget')}
          value={purchaseBudget}
          unit={UNIT.AMOUNT}
          infoText={t('summationCards.cards.purchasingBudget.infoText', 'Total inköpsbudget för alla inköpskategorier')}
          size="small"
        />
      )
    },
    {
      id: 'purchasingProfit',
      name: t('summationCards.cards.purchasingProfit.name', 'Inköpsvinst'),
      itemType: OverviewItemType.SMALL,
      component: (
        <SummaryCard
          title={t('summationCards.cards.purchasingProfit.title', 'Inköpsvinst')}
          value={totalPurchasingProfit}
          unit={UNIT.AMOUNT}
          infoText={t('summationCards.cards.purchasingProfit.infoText', 'Total inköpsvinst för alla inköpskategorier')}
          size="small"
        />
      )
    },
    {
      id: 'purchasingProfitPercentage',
      name: t('summationCards.cards.purchasingProfitPercentage.name', 'Total inköpsvinst %'),
      itemType: OverviewItemType.SMALL,
      component: (
        <SummaryCard
          title={t('summationCards.cards.purchasingProfitPercentage.title', 'Total inköpsvinst %')}
          value={purchasingProfitPercentage}
          unit={UNIT.PERCENTAGE}
          infoText={t('summationCards.cards.purchasingProfitPercentage.infoText', 'Total inköpsvinst i procent för alla inköpskategorier där det finns en kontraktssumma: (Budget - uppskattad äta % - kontraktssumma ) / (Budget - uppskattad äta % ) * 100 - Totalbudget. Räkna endast de budgetar som har en kontraktssumma')}
          size="small"
        />
      )
    },
    {
      id: 'totalBudget',
      name: t('summationCards.cards.totalBudget.name', 'Total budget'),
      itemType: OverviewItemType.SMALL,
      component: (
        <SummaryCard
          title={t('summationCards.cards.totalBudget.title', 'Total budget')}
          value={totalBudget}
          unit={UNIT.AMOUNT}
          infoText={t('summationCards.cards.totalBudget.infoText', 'Total budget för alla inköpskategorier')}
          size="small"
        />
      )
    },
    {
      id: 'totalETA',
      name: t('summationCards.cards.totalETA.name', 'Total ÄTA'),
      itemType: OverviewItemType.SMALL,
      component: (
        <SummaryCard
          title={t('summationCards.cards.totalETA.title', 'Total ÄTA')}
          value={totalETA}
          unit={UNIT.AMOUNT}
          infoText={t('summationCards.cards.totalETA.infoText', 'Total ÄTA för alla inköpskategorier')}
          size="small"
        />
      )
    }
  ], [totalContractSum, totalFinalCost, totalBudget, purchaseBudget, totalPurchasingProfit, purchasingProfitPercentage, totalETA, t]);

  const summationCardsPreference: (string | null)[] = appState.getSummationCardsPreferences();
  const [template, setTemplate] = useState<SummationCardType[]>([]);
  const [hasLoadedPreferences, setHasLoadedPreferences] = useState(false);

  const getCardFromId = useCallback((cardId: string | null) => {
    if (!cardId) return null;
    return cardOptions.find(option => option.id === cardId) || null;
  }, [cardOptions]);

  useEffect(() => {
    (async () => {
      if (!summationCardsPreference || summationCardsPreference.length === 0) {
        const defaultCards = [
          cardOptions.find(option => option.id === 'purchasingProfit')!, 
          cardOptions.find(option => option.id === 'totalBudget')!, 
          cardOptions.find(option => option.id === 'contractSum')!, 
          cardOptions.find(option => option.id === 'finalCost')!, 
          null 
        ];
        setTemplate(defaultCards);
      } else {
        const loadedTemplate = summationCardsPreference.map(getCardFromId).filter((item): item is SummationCardType => item !== null);
        const availableCards = cardOptions.filter(option => !loadedTemplate.find(item => item?.id === option.id));
        if (availableCards.length > 0) {
          setTemplate([...loadedTemplate, null]);
        } else {
          setTemplate(loadedTemplate);
        }
      }
      setHasLoadedPreferences(true);
    })();
  }, []);

  useEffect(() => {
    if (hasLoadedPreferences && summationCardsPreference && summationCardsPreference.length > 0) {
      const loadedTemplate = summationCardsPreference.map(getCardFromId).filter((item): item is SummationCardType => item !== null);
      const availableCards = cardOptions.filter(option => !loadedTemplate.find(item => item?.id === option.id));
      if (availableCards.length > 0) {
        setTemplate([...loadedTemplate, null]);
      } else {
        setTemplate(loadedTemplate);
      }
    }
  }, [summationCardsPreference, cardOptions, hasLoadedPreferences, getCardFromId]);

  useEffect(() => {
    if (template.length > 0 && hasLoadedPreferences) {
      setTemplate(prevTemplate => {
        const filteredTemplate = prevTemplate.filter(item => item !== null);
        const updatedTemplate = filteredTemplate.map(item => {
          const updatedOption = cardOptions.find(option => option.id === item.id);
          return updatedOption || item;
        });
        
        const availableCards = cardOptions.filter(option => !updatedTemplate.find(item => item?.id === option.id));
        if (availableCards.length > 0) {
          return [...updatedTemplate, null];
        } else {
          return updatedTemplate;
        }
      });
    }
  }, [cardOptions, hasLoadedPreferences]);

  const updateTemplate = (newTemplate: SummationCardType[]) => {
    const filteredTemplate = newTemplate.filter(item => item !== null);
    const availableCards = cardOptions.filter(option => !filteredTemplate.find(item => item?.id === option.id));
    
    if (availableCards.length > 0) {
      setTemplate([...filteredTemplate, null]);
    } else {
      setTemplate(filteredTemplate);
    }
    
    const finalTemplate = filteredTemplate.map(item => item.id);
    autosave(async () => await ServiceUser.updatePreferences(appState.getUserId(), finalTemplate, 'summationCards'));
    appState.setSummationCardsPreferences(finalTemplate);
  };

  const availableCards = useMemo(() => 
    cardOptions.filter(option => !template.find(item => item?.id === option.id))
  , [cardOptions, template]);

  return (
    <div className={styles.summationCards}>
      {template.map((item, index: number) => {
        return (
          item ? (
            <XIconWrapper key={item.id} onClick={() => {
              const newTemplate = template.filter((_, i) => i !== index);
              updateTemplate(newTemplate);
            }}>
              {item.component}
            </XIconWrapper>
          ) : availableCards.length > 0 && (
            <TemplateItem
              key={`item-index-${index}`}
              template={template}
              setTemplate={updateTemplate}
              index={index}
              options={availableCards}
              isMultipleRow={true}
            />
          )
        );
      })}
    </div>
  );
};

export default SummationCards;
